import { useState, useEffect, useCallback, useRef } from 'react';
import {
  BarChart3,
  FileText,
  DollarSign,
  Activity,
  Wrench,
  Fuel,
  ShieldCheck,
  X,
  Download,
  Printer,
  Loader2,
  ArrowLeft,
} from 'lucide-react';
import { apiFetch } from '../lib/api';

// ─── Types ──────────────────────────────────────────────────

type ReportKey = 'fleet-summary' | 'cost-analysis' | 'utilization' | 'maintenance-history' | 'fuel-trends' | 'compliance';

interface ReportCard {
  key: ReportKey;
  title: string;
  description: string;
  icon: typeof BarChart3;
  color: string;
}

const reports: ReportCard[] = [
  { key: 'fleet-summary', title: 'Fleet Summary', description: 'Overview of all vehicles, status distribution, and fleet health metrics.', icon: FileText, color: 'text-emerald-400' },
  { key: 'cost-analysis', title: 'Cost Analysis', description: 'Breakdown of maintenance, fuel, insurance, and operational costs per vehicle.', icon: DollarSign, color: 'text-amber-400' },
  { key: 'utilization', title: 'Utilization', description: 'Vehicle usage rates, idle time analysis, and assignment efficiency.', icon: Activity, color: 'text-blue-400' },
  { key: 'maintenance-history', title: 'Maintenance History', description: 'Complete service history, upcoming schedules, and cost trends over time.', icon: Wrench, color: 'text-purple-400' },
  { key: 'fuel-trends', title: 'Fuel Trends', description: 'Fuel consumption patterns, cost per mile tracking, and efficiency comparisons.', icon: Fuel, color: 'text-orange-400' },
  { key: 'compliance', title: 'Compliance', description: 'Inspection status, license expirations, regulatory compliance overview.', icon: ShieldCheck, color: 'text-cyan-400' },
];

// ─── CSV Export ──────────────────────────────────────────────

function downloadCsv(filename: string, headers: string[], rows: string[][]) {
  const escape = (v: string) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [headers.map(escape).join(','), ...rows.map((r) => r.map(escape).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Stat Card ──────────────────────────────────────────────

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-muted/40 px-4 py-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold text-foreground">{value}</div>
    </div>
  );
}

// ─── Table ──────────────────────────────────────────────────

function DataTable({ headers, rows }: { headers: string[]; rows: (string | number)[][] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm text-left">
        <thead className="bg-muted/60 text-xs text-muted-foreground uppercase">
          <tr>
            {headers.map((h) => (
              <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row, i) => (
            <tr key={i} className="hover:bg-muted/30">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-foreground whitespace-nowrap">{cell}</td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={headers.length} className="px-4 py-8 text-center text-muted-foreground">No data available</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Report Renderers ───────────────────────────────────────

/* eslint-disable @typescript-eslint/no-explicit-any */

function renderFleetSummary(data: any, onExportCsv: () => void) {
  const statusEntries = Object.entries(data.by_status ?? {}) as [string, number][];
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total Vehicles" value={data.total_vehicles} />
        <Stat label="Average Mileage" value={data.avg_mileage?.toLocaleString()} />
        <Stat label="Total Fuel Cost" value={`$${data.total_fuel_cost?.toLocaleString()}`} />
        {statusEntries.map(([status, count]) => (
          <Stat key={status} label={status.charAt(0).toUpperCase() + status.slice(1)} value={count} />
        ))}
      </div>
      <ExportBar onCsv={onExportCsv} />
    </>
  );
}

function renderCostAnalysis(data: any, onExportCsv: () => void) {
  const headers = ['Vehicle', '#', 'Miles', 'Fuel', 'Maintenance', 'Insurance', 'Total', '$/Mile'];
  const rows = (data.vehicles ?? []).map((v: any) => [
    v.vehicle_name, v.vehicle_number, v.total_miles?.toLocaleString(),
    `$${v.fuel_cost}`, `$${v.maintenance_cost}`, `$${v.insurance_cost}`,
    `$${v.total_cost}`, `$${v.cost_per_mile}`,
  ]);
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total Fuel" value={`$${data.total_fuel_cost?.toLocaleString()}`} />
        <Stat label="Total Maintenance" value={`$${data.total_maintenance_cost?.toLocaleString()}`} />
        <Stat label="Combined Cost" value={`$${data.total_cost?.toLocaleString()}`} />
        <Stat label="Vehicles" value={data.vehicle_count} />
      </div>
      <DataTable headers={headers} rows={rows} />
      <ExportBar onCsv={onExportCsv} />
    </>
  );
}

function renderUtilization(data: any, onExportCsv: () => void) {
  const headers = ['Vehicle', '#', 'Status', 'Mileage', 'Active', 'Driver'];
  const rows = (data.vehicles ?? []).map((v: any) => [
    v.vehicle_name, v.vehicle_number, v.status,
    v.mileage?.toLocaleString(), v.is_active ? 'Yes' : 'No',
    v.assigned_driver ?? '—',
  ]);
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Total" value={data.total} />
        <Stat label="Active" value={data.active} />
        <Stat label="Idle" value={data.idle} />
        <Stat label="Utilization" value={`${data.utilization_pct}%`} />
        <Stat label="Assignment Rate" value={`${data.assignment_rate_pct}%`} />
      </div>
      <DataTable headers={headers} rows={rows} />
      <ExportBar onCsv={onExportCsv} />
    </>
  );
}

function renderMaintenanceHistory(data: any, onExportCsv: () => void) {
  const headers = ['Vehicle', 'Type', 'Status', 'Vendor', 'Cost', 'Completed', 'Due'];
  const rows = (data.records ?? []).map((r: any) => [
    r.vehicle_name, r.maintenance_type, r.status,
    r.vendor ?? '—', `$${r.cost}`,
    r.completed_date?.slice(0, 10) ?? '—', r.due_date?.slice(0, 10) ?? '—',
  ]);

  const trendHeaders = ['Month', 'Count', 'Total Cost'];
  const trendRows = (data.cost_trend ?? []).map((t: any) => [t.month, t.count, `$${t.total_cost}`]);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat label="Total Records" value={data.total_records} />
        <Stat label="Total Cost" value={`$${data.total_cost?.toLocaleString()}`} />
        <Stat label="Months Tracked" value={data.cost_trend?.length ?? 0} />
      </div>
      {trendRows.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-2">Cost Trend by Month</h3>
          <DataTable headers={trendHeaders} rows={trendRows} />
        </div>
      )}
      <h3 className="text-sm font-medium text-muted-foreground mb-2">Service Records</h3>
      <DataTable headers={headers} rows={rows} />
      <ExportBar onCsv={onExportCsv} />
    </>
  );
}

function renderFuelTrends(data: any, onExportCsv: () => void) {
  const headers = ['Vehicle', '#', 'Gallons', 'Cost', 'Miles', 'MPG', '$/Mile'];
  const rows = (data.vehicles ?? []).map((v: any) => [
    v.vehicle_name, v.vehicle_number, v.total_gallons,
    `$${v.total_cost}`, v.total_miles?.toLocaleString(),
    v.avg_mpg, `$${v.cost_per_mile}`,
  ]);

  const trendHeaders = ['Month', 'Gallons', 'Cost', 'Fills'];
  const trendRows = (data.monthly_trend ?? []).map((t: any) => [t.month, t.total_gallons, `$${t.total_cost}`, t.fill_count]);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat label="Total Gallons" value={data.total_gallons?.toLocaleString()} />
        <Stat label="Total Cost" value={`$${data.total_cost?.toLocaleString()}`} />
        <Stat label="Fill Count" value={data.fill_count} />
      </div>
      {trendRows.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-2">Monthly Trend</h3>
          <DataTable headers={trendHeaders} rows={trendRows} />
        </div>
      )}
      <h3 className="text-sm font-medium text-muted-foreground mb-2">Per Vehicle</h3>
      <DataTable headers={headers} rows={rows} />
      <ExportBar onCsv={onExportCsv} />
    </>
  );
}

function renderCompliance(data: any, onExportCsv: () => void) {
  const statusEntries = Object.entries(data.by_status ?? {}) as [string, number][];

  const expiringHeaders = ['Type', 'Description', 'Vehicle', 'Expiry', 'Days Left', 'Status'];
  const expiringRows = (data.expiring_soon ?? []).map((i: any) => [
    i.item_type, i.description, i.vehicle_name ?? '—',
    i.expiry_date?.slice(0, 10), i.days_until_expiry, i.status,
  ]);

  const inspHeaders = ['Vehicle', 'Type', 'Status', 'Date', 'Expiry'];
  const inspRows = (data.recent_inspections ?? []).map((i: any) => [
    i.vehicle_name, i.inspection_type, i.status,
    i.inspection_date?.slice(0, 10), i.expiry_date?.slice(0, 10) ?? '—',
  ]);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Compliance Rate" value={`${data.compliance_pct}%`} />
        <Stat label="Total Items" value={data.total_items} />
        {statusEntries.map(([status, count]) => (
          <Stat key={status} label={status.replace(/_/g, ' ')} value={count} />
        ))}
      </div>
      {expiringRows.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-2">Expiring Soon (90 days)</h3>
          <DataTable headers={expiringHeaders} rows={expiringRows} />
        </div>
      )}
      <h3 className="text-sm font-medium text-muted-foreground mb-2">Recent Inspections</h3>
      <DataTable headers={inspHeaders} rows={inspRows} />
      <ExportBar onCsv={onExportCsv} />
    </>
  );
}

/* eslint-enable @typescript-eslint/no-explicit-any */

// ─── Export Bar ─────────────────────────────────────────────

function ExportBar({ onCsv }: { onCsv: () => void }) {
  return (
    <div className="flex gap-2 pt-2">
      <button onClick={onCsv} className="flex items-center gap-1.5 rounded-lg border border-border bg-muted px-3 py-1.5 text-xs text-foreground hover:bg-muted transition-colors">
        <Download className="h-3.5 w-3.5" /> Export CSV
      </button>
      <button onClick={() => window.print()} className="flex items-center gap-1.5 rounded-lg border border-border bg-muted px-3 py-1.5 text-xs text-foreground hover:bg-muted transition-colors">
        <Printer className="h-3.5 w-3.5" /> Export PDF
      </button>
    </div>
  );
}

// ─── Report View (slide-over) ───────────────────────────────

function ReportView({ report, onClose }: { report: ReportCard; onClose: () => void }) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiFetch<any>(`/api/reports/${report.key}`);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [report.key]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const exportCsv = useCallback(() => {
    if (!data) return;
    // Extract the first array-of-objects property for CSV
    const arrayKey = Object.keys(data).find((k) => Array.isArray(data[k]) && data[k].length > 0 && typeof data[k][0] === 'object');
    if (!arrayKey) return;
    const items = data[arrayKey] as Record<string, unknown>[];
    const headers = Object.keys(items[0]);
    const rows = items.map((item) => headers.map((h) => String(item[h] ?? '')));
    downloadCsv(`${report.key}-report.csv`, headers, rows);
  }, [data, report.key]);

  const renderContent = () => {
    if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
    if (error) return <div className="rounded-lg border border-red-900/50 bg-red-950/30 px-4 py-3 text-sm text-red-400">{error}</div>;
    if (!data) return null;

    switch (report.key) {
      case 'fleet-summary': return renderFleetSummary(data, exportCsv);
      case 'cost-analysis': return renderCostAnalysis(data, exportCsv);
      case 'utilization': return renderUtilization(data, exportCsv);
      case 'maintenance-history': return renderMaintenanceHistory(data, exportCsv);
      case 'fuel-trends': return renderFuelTrends(data, exportCsv);
      case 'compliance': return renderCompliance(data, exportCsv);
      default: return null;
    }
  };

  const Icon = report.icon;

  return (
    <div className="fixed inset-0 z-50 flex print:static print:block">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 print:hidden" onClick={onClose} />

      {/* Panel */}
      <div ref={panelRef} className="relative ml-auto w-full max-w-4xl overflow-y-auto bg-background border-l border-border shadow-2xl print:ml-0 print:max-w-none print:border-0 print:shadow-none">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background/95 backdrop-blur px-6 py-4 print:static print:bg-background print:border-border">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-muted transition-colors print:hidden">
              <ArrowLeft className="h-4 w-4 text-muted-foreground" />
            </button>
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted/80 print:bg-card">
              <Icon className={`h-5 w-5 ${report.color} print:text-muted-foreground`} />
            </div>
            <h2 className="text-lg font-semibold text-foreground print:text-foreground">{report.title}</h2>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-muted transition-colors print:hidden">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        <div className="space-y-5 p-6 print:p-4">{renderContent()}</div>
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────

export default function ReportsPage() {
  const [selectedReport, setSelectedReport] = useState<ReportCard | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <BarChart3 className="h-6 w-6 text-emerald-500" />
        <h1 className="text-2xl font-bold text-foreground">Fleet Reports</h1>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {reports.map((report) => (
          <button
            key={report.key}
            onClick={() => setSelectedReport(report)}
            className="rounded-xl border border-border bg-card/50 backdrop-blur-sm p-6 flex flex-col gap-4 text-left hover:border-border hover:bg-card/80 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted/80">
                <report.icon className={`h-5 w-5 ${report.color}`} />
              </div>
              <h3 className="text-lg font-semibold text-foreground">{report.title}</h3>
            </div>
            <p className="text-sm text-muted-foreground flex-1">{report.description}</p>
            <span className="text-xs font-medium text-emerald-400">View Report &rarr;</span>
          </button>
        ))}
      </div>

      {selectedReport && <ReportView report={selectedReport} onClose={() => setSelectedReport(null)} />}
    </div>
  );
}
